import { create } from 'zustand';

export type VoiceConnectionPhase = 'idle' | 'connecting' | 'connected' | 'reconnecting';

/**
 * Режим рации («говорить, пока держу пробел») убран 02.10.2026 по решению владельца. Выбор жил в
 * браузере, и у тех, кто его включал, он остался бы работать без кнопки выключения: микрофон молчал
 * бы до пробела, а переключить было бы нечем. Поэтому запись стирается при загрузке.
 */
try {
  globalThis.localStorage?.removeItem('voice.pushToTalk');
} catch {
  // Хранилище запрещено – значит, и рации там не было.
}

const LAST_VOICE_SESSION_KEY = 'voice.lastSession';

/**
 * Сессия, в голосе которой я был и откуда не уходил сам.
 *
 * Единственная запись голоса, которая обязана пережить перезагрузку страницы. По ней комната узнаёт
 * себя, а `useVoiceAutoRejoin` возвращает в голос сам – после перезагрузки, перезапуска приложения
 * и обрыва связи (с 02.10.2026; до этого возврат был только по нажатию «Вернуться»).
 *
 * Хранилище, а не стор: всё остальное в сторе — про живое соединение, а этой записи нужен ровно
 * тот момент, когда живого соединения уже нет и вкладки, которая его держала, тоже.
 */
const readLastVoiceSession = (): string | null => {
  try {
    return globalThis.localStorage?.getItem(LAST_VOICE_SESSION_KEY) ?? null;
  } catch {
    return null;
  }
};

const writeLastVoiceSession = (sessionId: string | null) => {
  try {
    if (sessionId) {
      globalThis.localStorage?.setItem(LAST_VOICE_SESSION_KEY, sessionId);
    } else {
      globalThis.localStorage?.removeItem(LAST_VOICE_SESSION_KEY);
    }
  } catch {
    // Запрет хранилища стоит одного предложения вернуться, а не поломки входа в голос.
  }
};

const MIC_KEY = 'voice.lastMicOn';

/**
 * Микрофон, с которым я был в голосе. Возврат – сам после сбоя или кнопкой «Вернуть сюда» –
 * идёт с ним: выключенный молча не включаем (02.10.2026).
 */
export const readLastMicOn = (): boolean => {
  try {
    return globalThis.localStorage?.getItem(MIC_KEY) !== '0';
  } catch {
    return true;
  }
};

export const writeLastMicOn = (on: boolean) => {
  try {
    globalThis.localStorage?.setItem(MIC_KEY, on ? '1' : '0');
  } catch {
    // Хранилище запрещено – вернёмся с открытым микрофоном, как при обычном входе.
  }
};

export type VoiceRoomInfo = {
  sessionId: string;
  postId: string;
  hostId: string | null;
  livekitUrl: string;
  token: string;
  maxParticipants: number | null;
  /**
   * Игра и хозяин сессии — подпись для плавающего окна. Кладутся сюда в момент входа, потому что
   * окно живёт в корне лейаута и данных поста не видит: тянуть их отдельным запросом ради двух
   * строк подписи незачем.
   */
  gameName?: string | null;
  hostName?: string | null;
};

/**
 * Комната, из которой человека вынул личный звонок. Помним её не ради автовозврата — он
 * запрещён сознательно (см. useVoiceCallArbitration), — а ради одной кнопки на плашке «Звонок
 * завершён»: без неё разговор с командой обрывается звонком навсегда и молча.
 */
export type InterruptedVoiceRoom = {
  sessionId: string;
  postId: string;
  gameName: string | null;
  hostName: string | null;
};

/**
 * Участник комнаты глазами интерфейса. Присутствие приходит из двух источников: список от сервера
 * (кто вообще относится к комнате) и живое состояние LiveKit (кто подключён и кто говорит прямо
 * сейчас). Второе обновляется десятки раз в секунду, поэтому держится отдельно от серверных данных.
 */
export type VoiceMember = {
  userId: string;
  connected: boolean;
  host: boolean;
  speaking: boolean;
  muted: boolean;
  /**
   * Имя и аватар приходят не с сервера, а из токена LiveKit — там они уже есть у каждого
   * подключённого. Отдельного запроса профилей ради списка на четыре строки не делаем.
   */
  displayName?: string | null;
  avatar?: string | null;
};

/**
 * Качество связи глазами интерфейса. Хорошая связь молчит, нестабильная предупреждает, потерянная
 * уводит виджет в переподключение — трёх состояний хватает, шкала из пяти делений человеку
 * посреди катки ничего не даёт.
 */
export type VoiceQuality = 'unknown' | 'good' | 'unstable' | 'lost';

type VoiceSlice = {
  phase: VoiceConnectionPhase;
  room: VoiceRoomInfo | null;
  members: VoiceMember[];
  micEnabled: boolean;
  /**
   * Микрофон не отдала система, а не выключил человек. Живёт в сторе, а не в хуке комнаты,
   * потому что об этом обязаны знать оба места: и виджет внутри разговора, и карточка сессии —
   * иначе карточка продолжает называть системный запрет собственным выбором игрока.
   */
  micDenied: boolean;
  quality: VoiceQuality;
  /** Локальная громкость участников, 0–1. Касается только меня — модерация живёт отдельно. */
  localVolumes: Record<string, number>;
  /**
   * Кого я не слышу. Отдельно от громкости, а не как «громкость = 0»: громкость `<audio>` меняется
   * только на десктопе, а `muted` работает везде — на телефоне это единственный способ действительно
   * заглушить человека.
   */
  localMuted: Record<string, boolean>;
  /**
   * Последний отказ: код и сессия, в которую не пустили. Без сессии код был бы глобальным, и
   * «тебя выгнали» с одной карточки показывалось бы на всех остальных.
   */
  lastError: string | null;
  lastErrorSessionId: string | null;
  /**
   * Комната, покинутая ради звонка. Живёт отдельно от `room`, потому что обязана пережить
   * `reset`: `reset` гасит разговор, а это — память о нём, и нужна она уже после того, как
   * разговора нет.
   */
  interruptedRoom: InterruptedVoiceRoom | null;
  /**
   * Сессия, в голосе которой я был и из которой не выходил. Переживает перезагрузку страницы —
   * это единственное, чем комната отличает «голос не восстановился» от «я туда и не заходил».
   *
   * Ставится входом, снимается любым осознанным выходом (сам вышел, вышел из аккаунта) и концом
   * сессии. Разрыв связи её НЕ снимает — ради него она и заведена.
   */
  lastVoiceSessionId: string | null;

  setConnecting: (room: VoiceRoomInfo) => void;
  setConnected: () => void;
  setReconnecting: () => void;
  setMembers: (members: VoiceMember[]) => void;
  /** Точечное обновление живого состояния — не трогает состав, только флаги. */
  updateLiveState: (state: {
    speaking: string[];
    connected: string[];
    muted?: string[];
    profiles?: Record<string, { displayName?: string | null; avatar?: string | null }>;
  }) => void;
  setMicEnabled: (enabled: boolean) => void;
  setMicDenied: (denied: boolean) => void;
  setQuality: (quality: VoiceQuality) => void;
  setLocalVolume: (userId: string, volume: number) => void;
  setLocalMuted: (userId: string, muted: boolean) => void;
  setError: (code: string | null, sessionId?: string | null) => void;
  /** Запомнить комнату, из которой вышли не по своей воле. Ставит только арбитраж со звонком. */
  rememberInterruptedRoom: (room: VoiceRoomInfo) => void;
  forgetInterruptedRoom: () => void;
  /**
   * Забыть, что я был в голосе. Зовут ровно два места: общий выход `leaveVoice` (он же выход из
   * аккаунта и размонтирование хоста) и комната, когда сессия закончилась. Звать «вернуться»
   * туда, откуда человек ушёл нарочно, — ложь.
   */
  forgetVoiceSession: () => void;
  reset: () => void;
};

export const useVoiceStore = create<VoiceSlice>()((set, get) => ({
  phase: 'idle',
  room: null,
  members: [],
  micEnabled: true,
  micDenied: false,
  quality: 'unknown',
  localVolumes: {},
  localMuted: {},
  lastError: null,
  lastErrorSessionId: null,
  interruptedRoom: null,
  lastVoiceSessionId: readLastVoiceSession(),

  setConnecting: (room) => {
    // Отметка ставится входом, а не выходом: «я был в голосе» становится правдой ровно здесь, и
    // ни один путь выхода — включая закрытие вкладки, о котором нас никто не предупредит, — эту
    // запись сделать уже не сможет.
    writeLastVoiceSession(room.sessionId);
    set({
      phase: 'connecting',
      room,
      lastVoiceSessionId: room.sessionId,
      lastError: null,
      lastErrorSessionId: null,
      // Вошли в комнату — предлагать «вернуться» больше не во что: человек либо уже вернулся,
      // либо ушёл в другую сессию. Протухшее предложение хуже, чем никакого.
      interruptedRoom: null,
      micEnabled: true,
      micDenied: false,
      quality: 'unknown',
    });
  },

  setConnected: () => set({ phase: 'connected' }),

  // Переподключение — состояние SDK, а не наше решение: комната и состав при этом сохраняются,
  // иначе виджет мигал бы пустым списком на каждом провале сети.
  setReconnecting: () => set({ phase: 'reconnecting' }),

  setMembers: (members) => set({ members }),

  updateLiveState: ({ speaking, connected, muted, profiles }) => {
    const speakingSet = new Set(speaking);
    const connectedSet = new Set(connected);
    const mutedSet = new Set(muted ?? []);
    const hostId = get().room?.hostId ?? null;

    const known = get().members.map((member) => {
      const profile = profiles?.[member.userId];
      return {
        ...member,
        speaking: speakingSet.has(member.userId),
        connected: connectedSet.has(member.userId),
        muted: mutedSet.has(member.userId),
        // Имя, добытое однажды, не теряем: отключившийся участник остаётся в списке, и подменять
        // его имя на UUID в момент выхода — худшее, что можно сделать.
        displayName: profile?.displayName ?? member.displayName ?? null,
        avatar: profile?.avatar ?? member.avatar ?? null,
      };
    });

    // Зашедшие ПОСЛЕ меня в списке с сервера отсутствуют по построению: он снят в момент моего
    // входа. Пока их не добавляли здесь, зашедший первым не видел вообще никого и навсегда
    // оставался «1 из N» — при том, что второй его прекрасно видел. Медиасервер знает про каждого
    // подключённого, включая имя и аватар из токена, поэтому достроить строку есть из чего.
    const seen = new Set(known.map((member) => member.userId));
    const arrived = connected
      .filter((userId) => !seen.has(userId))
      .map((userId) => ({
        userId,
        connected: true,
        host: hostId != null && userId === hostId,
        speaking: speakingSet.has(userId),
        muted: mutedSet.has(userId),
        displayName: profiles?.[userId]?.displayName ?? null,
        avatar: profiles?.[userId]?.avatar ?? null,
      }));

    set({ members: arrived.length > 0 ? [...known, ...arrived] : known });
  },

  setMicEnabled: (micEnabled) => set({ micEnabled }),
  setMicDenied: (micDenied) => set({ micDenied }),
  setQuality: (quality) => set({ quality }),
  setLocalVolume: (userId, volume) =>
    set({ localVolumes: { ...get().localVolumes, [userId]: volume } }),
  setLocalMuted: (userId, muted) => set({ localMuted: { ...get().localMuted, [userId]: muted } }),
  setError: (lastError, lastErrorSessionId = null) => set({ lastError, lastErrorSessionId }),

  rememberInterruptedRoom: (room) =>
    set({
      interruptedRoom: {
        sessionId: room.sessionId,
        postId: room.postId,
        gameName: room.gameName ?? null,
        hostName: room.hostName ?? null,
      },
    }),
  forgetInterruptedRoom: () => set({ interruptedRoom: null }),

  forgetVoiceSession: () => {
    writeLastVoiceSession(null);
    set({ lastVoiceSessionId: null });
  },

  // `interruptedRoom` здесь намеренно не трогаем: `reset` вызывается внутри `leaveVoice` — ровно
  // в тот момент, когда память о покинутой комнате только что записали. Чистит её сам
  // `leaveVoice` (при обычном выходе), вход в новую комнату и плашка, когда её убрали.
  //
  // `lastVoiceSessionId` — тем более: `reset` зовёт и разрыв связи (`RoomEvent.Disconnected`), а
  // это ровно тот случай, ради которого отметка и живёт. Снимает её `forgetVoiceSession`.
  reset: () =>
    set({
      phase: 'idle',
      room: null,
      members: [],
      micEnabled: true,
      micDenied: false,
      quality: 'unknown',
      localVolumes: {},
      localMuted: {},
      lastError: null,
      lastErrorSessionId: null,
    }),
}));

/** Мы сейчас в голосе этой сессии? Нужен карточкам, чтобы показать «выйти» вместо «зайти». */
export const useIsInVoiceRoom = (sessionId: string | null | undefined) =>
  useVoiceStore(
    (state) => Boolean(sessionId) && state.room?.sessionId === sessionId && state.phase !== 'idle'
  );
